/* Favourites, kept on the device.
 *
 * The artboard hearts cards and shops. Nothing server-side holds a favourite and
 * nothing needs to: it is a person's own ordering of their own wallet, on the
 * phone they carry. Kept in localStorage, read once at start, and written on
 * every change; a cleared browser loses an ordering preference and no stamps.
 *
 * Shops cannot be hearted, because none is listed. Cards can.
 */
import { useCallback, useState } from "react";

const KEY = "damga.favs.v1";

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    // Private browsing, or storage disabled. No favourites, and the wallet is
    // otherwise untouched.
    return [];
  }
}

export function useFavourites() {
  const [ids, setIds] = useState<string[]>(read);

  const toggle = useCallback((id: string) => {
    let nowOn = false;
    setIds((current) => {
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      nowOn = next.includes(id);
      try {
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        /* The list still works for this session. */
      }
      return next;
    });
    return () => nowOn;
  }, []);

  return {
    ids,
    has: (id: string) => ids.includes(id),
    /** Returns whether it is now a favourite, for the toast. */
    toggle: (id: string) => {
      const was = ids.includes(id);
      toggle(id);
      return !was;
    },
  };
}
